import React, { useState } from 'react'
import {
  Container,
  Breadcrumb,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  Badge,
} from 'react-bootstrap'
import { Link, useParams, useHistory } from 'react-router-dom'
import useTerminalDetail from 'hooks/use-terminal-detail'
import useAnsibleDetail from 'hooks/use-ansible-detail'
import useMacMiniDetail from 'hooks/use-mac-mini-detail'
import { DeviceStatusUpdateModal } from 'components/device-status'
import { PnsMacMiniStatus } from 'interfaces/pns-mac-mini'
import toast from 'react-hot-toast'

const InventoryMacMiniDetailPage = () => {
  const { terminalId, ansibleId, macMiniId } = useParams<{
    terminalId: string
    ansibleId?: string
    macMiniId: string
  }>()
  const history = useHistory()
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false)

  const { data: terminal } = useTerminalDetail(terminalId)

  const { data: ansible } = useAnsibleDetail(ansibleId || '')

  const {
    data: macMini,
    isLoading: macMiniLoading,
    error: macMiniError,
  } = useMacMiniDetail(macMiniId)

  const handleViewUsers = () => {
    if (ansibleId) {
      // Ansible-assigned Mac Mini
      history.push(
        `/inventory/terminals/${terminalId}/ansibles/${ansibleId}/mac-mini/${macMiniId}/users`
      )
    } else {
      // Received Mac Mini - could add different route or disable button
      toast('User management not available for received Mac Minis')
    }
  }

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success'
      case 'AVAILABLE':
        return 'info'
      case 'PROVISIONING':
        return 'info'
      case 'MAINTENANCE':
        return 'warning'
      case 'RELOCATING':
        return 'warning'
      case 'OFFLINE':
        return 'secondary'
      case 'DECOMMISSIONED':
        return 'danger'
      default:
        return 'secondary'
    }
  }

  if (macMiniError) {
    return (
      <Container>
        <Alert variant="danger">
          Error loading Mac Mini details:{' '}
          {macMiniError instanceof Error
            ? macMiniError.message
            : 'Unknown error'}
        </Alert>
      </Container>
    )
  }

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/inventory">Inventory</Breadcrumb.Item>
        <Breadcrumb.Item href={`/inventory/terminals/${terminalId}`}>
          {terminal?.name || 'Terminal'}
        </Breadcrumb.Item>
        {ansibleId && (
          <Breadcrumb.Item
            href={`/inventory/terminals/${terminalId}/ansibles/${ansibleId}`}
          >
            {ansible?.name || 'Ansible'}
          </Breadcrumb.Item>
        )}
        <Breadcrumb.Item active>{macMini?.name || 'Mac Mini'}</Breadcrumb.Item>
      </Breadcrumb>

      {macMiniLoading ? (
        <div className="text-center">
          <Spinner animation="border" />
          <div className="mt-2">Loading Mac Mini details...</div>
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>{macMini?.name}</h1>
            <div className="d-flex align-items-center gap-2">
              <Badge
                variant={getStatusVariant(macMini?.status || '')}
                className="fs-6"
              >
                {macMini?.status}
              </Badge>
              {macMini?.status && macMini.status !== PnsMacMiniStatus.RECEIVED && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setShowStatusUpdateModal(true)}
                >
                  Change Status
                </Button>
              )}
            </div>
          </div>

          {/* Mac Mini Information Card */}
          <Card className="mb-4">
            <Card.Header>
              <h4>Mac Mini Information</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p>
                    <strong>Name:</strong> {macMini?.name}
                  </p>
                  <p>
                    <strong>Model:</strong> {macMini?.model}
                  </p>
                  <p>
                    <strong>Serial Number:</strong> {macMini?.serialNumber}
                  </p>
                  <p>
                    <strong>Status:</strong> {macMini?.status}
                  </p>
                  <p>
                    <strong>OS Version:</strong> {macMini?.osVersion}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Current Users:</strong> {macMini?.currentUsers}
                  </p>
                  <p>
                    <strong>Max Users:</strong> {macMini?.maxUsers}
                  </p>
                  <p>
                    <strong>Available Slots:</strong>{' '}
                    {macMini?.availableUserSlots}
                  </p>
                  <p>
                    <strong>Primary IP:</strong> {macMini?.primaryIp}
                  </p>
                  <p>
                    <strong>Mac Address:</strong> {macMini?.macAddress}
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Performance Metrics Card */}
          <Card className="mb-4">
            <Card.Header>
              <h4>Performance Metrics</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <p>
                    <strong>CPU Usage:</strong> {macMini?.cpuUsage?.toFixed(1)}%
                  </p>
                </Col>
                <Col md={4}>
                  <p>
                    <strong>Memory Usage:</strong>{' '}
                    {macMini?.memoryUsage?.toFixed(1)}%
                  </p>
                </Col>
                <Col md={4}>
                  <p>
                    <strong>Disk Usage:</strong>{' '}
                    {macMini?.diskUsage?.toFixed(1)}%
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* User Management Card */}
          <Card className="mb-4">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h4>User Management</h4>
                {ansibleId ? (
                  <Button
                    variant="primary"
                    onClick={handleViewUsers}
                    disabled={
                      !macMini?.currentUsers || macMini.currentUsers === 0
                    }
                  >
                    View Users ({macMini?.currentUsers || 0})
                  </Button>
                ) : (
                  <Badge variant="secondary">
                    Received - Not yet assigned to Ansible
                  </Badge>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              <p>
                This Mac Mini currently hosts{' '}
                <strong>{macMini?.currentUsers || 0}</strong> user(s) out of a
                maximum of <strong>{macMini?.maxUsers || 0}</strong>.
              </p>
              {macMini?.isAvailable ? (
                <Alert variant="success">
                  This Mac Mini has available capacity for more users.
                </Alert>
              ) : (
                <Alert variant="warning">
                  This Mac Mini is at full capacity.
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* Location Information Card */}
          <Card className="mb-4">
            <Card.Header>
              <h4>Location Information</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p>
                    <strong>Terminal:</strong> {terminal?.name}
                  </p>
                  {macMini?.ansibleId ? (
                    <p>
                      <strong>Ansible:</strong> {ansible?.name}
                    </p>
                  ) : (
                    <p>
                      <strong>Status:</strong> Not assigned to Ansible
                    </p>
                  )}
                  <p>
                    <strong>Last Ansible Change:</strong>{' '}
                    {macMini?.lastAnsibleChange
                      ? new Date(macMini.lastAnsibleChange).toLocaleString()
                      : 'N/A'}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Terminal ID:</strong> {macMini?.terminalId || 'N/A'}
                  </p>
                  <p>
                    <strong>Ansible ID:</strong> {macMini?.ansibleId || 'N/A'}
                  </p>
                  <p>
                    <strong>Last Health Check:</strong>{' '}
                    {macMini?.lastHealthCheck
                      ? new Date(macMini.lastHealthCheck).toLocaleString()
                      : 'N/A'}
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Device Status Update Modal */}
          {macMini && (
            <DeviceStatusUpdateModal
              show={showStatusUpdateModal}
              onHide={() => setShowStatusUpdateModal(false)}
              deviceId={macMini.id}
              deviceName={macMini.name}
              deviceType="mac-mini"
              currentStatus={macMini.status}
              macMini={macMini}
              onSuccess={() => {
                // Refresh page to show updated status
                window.location.reload()
              }}
            />
          )}
        </>
      )}
    </Container>
  )
}

export default InventoryMacMiniDetailPage
